import React, { useState, useEffect } from 'react';
import { useNavigate, useParams  } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import inventoryApi from '../../../services/inventoryApi';;

const ReturnTypesForm = ({ returnType, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const handleBack = useDetailBack('/inventory/return-types');
  const { id } = useParams();
  const isEditMode = Boolean(id && id !== 'new');

  const defaultFormData = {
    code: '',
    name: '',
    description: '',
    category: 'GENERAL',
    reasonCodes: [],
    restockFee: { type: 'PERCENTAGE', value: 0, tiers: [] },
    warrantyConfiguration: { applicableToWarranty: false, warrantyCoverage: 'NONE', maxWarrantyPeriod: 0 },
    processingTime: { minDays: 1, maxDays: 7 },
    refundPolicy: { allowRefund: true, refundPercentage: 100, refundConditions: [] },
    replacementPolicy: { allowReplacement: true, replacementConditions: [] },
    requirements: { originalPackaging: false, receiptRequired: true, inspectionRequired: false, authorizationRequired: false },
    isActive: true,
    notifications: { notifyCustomer: true, notifyManager: false }
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const mergeWithDefaults = (data, defaults) => {
    const merged = { ...defaults };
    Object.keys(data || {}).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        if (typeof data[key] === 'object' && !Array.isArray(data[key]) && defaults[key]) {
          merged[key] = mergeWithDefaults(data[key], defaults[key]);
        } else {
          merged[key] = data[key];
        }
      }
    });
    return merged;
  };

  const categories = [
    { value: 'GENERAL', label: 'General Return' },
    { value: 'DEFECTIVE', label: 'Defective Product' },
    { value: 'WARRANTY', label: 'Warranty Claim' },
    { value: 'EXCHANGE', label: 'Product Exchange' },
    { value: 'DAMAGED_SHIPPING', label: 'Damaged in Shipping' },
    { value: 'WRONG_ITEM', label: 'Wrong Item Sent' },
    { value: 'CUSTOMER_CHANGE', label: 'Customer Change of Mind' }
  ];

  const restockFeeTypes = [
    { value: 'PERCENTAGE', label: 'Percentage (%)' },
    { value: 'FIXED', label: 'Fixed Amount' },
    { value: 'TIERED', label: 'Tiered (by value)' },
    { value: 'NONE', label: 'No Fee' }
  ];

  useEffect(() => {
    if (returnType) {
      setFormData(mergeWithDefaults(returnType, defaultFormData));
    } else if (isEditMode && id) {
      fetchReturnType();
    } else {
      setFormData(defaultFormData);
    }
  }, [returnType, id]);

  const fetchReturnType = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getReturnTypeById(id);
      setFormData(mergeWithDefaults(response.data, defaultFormData));
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || 'Failed to fetch return type' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.code?.trim()) newErrors.code = 'Code is required';
    else if (!/^[A-Z0-9\-_]{2,20}$/.test(formData.code)) newErrors.code = 'Code must be 2-20 chars, uppercase alphanumeric with dashes/underscores';
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (formData.restockFee?.type === 'PERCENTAGE' && (formData.restockFee?.value < 0 || formData.restockFee?.value > 100)) newErrors['restockFee.value'] = 'Percentage must be 0–100';
    if (formData.restockFee?.type === 'FIXED' && formData.restockFee?.value < 0) newErrors['restockFee.value'] = 'Cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (errors.submit) setErrors(prev => ({ ...prev, submit: '' }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
    if (errors[`${parent}.${field}`]) setErrors(prev => ({ ...prev, [`${parent}.${field}`]: '' }));
  };

  const addReasonCode = () => {
    setFormData(prev => ({
      ...prev,
      reasonCodes: [...(prev.reasonCodes || []), { code: '', description: '', applicableCategories: ['GENERAL'], isActive: true }]
    }));
  };

  const removeReasonCode = (index) => {
    setFormData(prev => ({ ...prev, reasonCodes: (prev.reasonCodes || []).filter((_, i) => i !== index) }));
  };

  const handleReasonCodeChange = (index, field, value) => {
    const updated = [...(formData.reasonCodes || [])];
    updated[index] = { ...(updated[index] || {}), [field]: value };
    setFormData(prev => ({ ...prev, reasonCodes: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const submitData = { ...formData, code: formData.code.toUpperCase() };
      if (returnType || isEditMode) {
        await inventoryApi.updateReturnType(returnType?._id || id, submitData);
      } else {
        await inventoryApi.createReturnType(submitData);
      }
      if (onSuccess) onSuccess();
      else handleBack();
    } catch (error) {
      const errorResponse = error.response?.data;
      const serverErrors = {};
      if (errorResponse?.error) {
        if (errorResponse.error.includes('Return type with this code already exists') || errorResponse.details?.includes('code_1 dup key')) {
          serverErrors.code = 'Return type with this code already exists';
        } else {
          serverErrors.submit = errorResponse.error;
        }
      } else {
        serverErrors.submit = error.response?.data?.message || error.message || 'Failed to save return type';
      }
      setErrors(serverErrors);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else handleBack();
  };

  const inputClass = "w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-600 mb-1";
  const errorClass = "mt-0.5 text-xs text-red-500";

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  {returnType || isEditMode ? 'Edit Return Type' : 'Create Return Type'}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCancel}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="px-4 pt-3 pb-2 space-y-3">
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs text-red-700">{errors.submit}</p>
                </div>
              )}

              {/* Code + Name */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Code <span className="text-red-500">*</span></label>
                  <input
                    name="code"
                    value={formData.code || ''}
                    onChange={handleChange}
                    disabled={!!(returnType || isEditMode)}
                    placeholder="e.g. RET-DEF"
                    className={`${inputClass} uppercase ${errors.code ? 'border-red-400' : ''} ${(returnType || isEditMode) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {errors.code && <p className={errorClass}>{errors.code}</p>}
                </div>
                <div>
                  <label className={labelClass}>Name <span className="text-red-500">*</span></label>
                  <input
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    placeholder="e.g. Defective Product Return"
                    className={`${inputClass} ${errors.name ? 'border-red-400' : ''}`}
                  />
                  {errors.name && <p className={errorClass}>{errors.name}</p>}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  name="description"
                  rows={2}
                  value={formData.description || ''}
                  onChange={handleChange}
                  placeholder="Brief description of this return type..."
                  className={inputClass}
                />
              </div>

              {/* Category + Restock Fee */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={labelClass}>Category</label>
                  <select name="category" value={formData.category || 'GENERAL'} onChange={handleChange} className={inputClass}>
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Restock Fee</label>
                  <select
                    value={formData.restockFee?.type || 'PERCENTAGE'}
                    onChange={(e) => handleNestedChange('restockFee', 'type', e.target.value)}
                    className={inputClass}
                  >
                    {restockFeeTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                {formData.restockFee?.type !== 'NONE' && formData.restockFee?.type !== 'TIERED' && (
                  <div>
                    <label className={labelClass}>Fee Value</label>
                    <input
                      type="number"
                      placeholder={formData.restockFee?.type === 'PERCENTAGE' ? '0–100' : 'Amount'}
                      value={formData.restockFee?.value || 0}
                      onChange={(e) => handleNestedChange('restockFee', 'value', parseFloat(e.target.value) || 0)}
                      min="0"
                      max={formData.restockFee?.type === 'PERCENTAGE' ? 100 : undefined}
                      className={`${inputClass} ${errors['restockFee.value'] ? 'border-red-400' : ''}`}
                    />
                    {errors['restockFee.value'] && <p className={errorClass}>{errors['restockFee.value']}</p>}
                  </div>
                )}
              </div>

              {/* Processing Time + Requirements */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Processing Time (Days)</label>
                  <div className="grid grid-cols-2 gap-1">
                    <input
                      type="number"
                      placeholder="Min"
                      value={formData.processingTime?.minDays || 1}
                      onChange={(e) => handleNestedChange('processingTime', 'minDays', parseInt(e.target.value) || 1)}
                      min="1"
                      className={inputClass}
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={formData.processingTime?.maxDays || 7}
                      onChange={(e) => handleNestedChange('processingTime', 'maxDays', parseInt(e.target.value) || 7)}
                      min="1"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Requirements</label>
                  <div className="grid grid-cols-2 gap-1">
                    {[
                      { key: 'receiptRequired', label: 'Receipt' },
                      { key: 'originalPackaging', label: 'Orig. Packaging' },
                      { key: 'inspectionRequired', label: 'Inspection' },
                      { key: 'authorizationRequired', label: 'Authorization' }
                    ].map(req => (
                      <label key={req.key} className="flex items-center gap-1 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.requirements?.[req.key] || false}
                          onChange={(e) => handleNestedChange('requirements', req.key, e.target.checked)}
                          className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                        />
                        <span className="text-gray-700">{req.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Policies */}
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-1.5">
                  <input
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive || false}
                    onChange={handleChange}
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={formData.refundPolicy?.allowRefund || false}
                    onChange={(e) => handleNestedChange('refundPolicy', 'allowRefund', e.target.checked)}
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-700">Allow Refunds</span>
                  {formData.refundPolicy?.allowRefund && (
                    <input
                      type="number"
                      placeholder="%"
                      value={formData.refundPolicy?.refundPercentage || 100}
                      onChange={(e) => handleNestedChange('refundPolicy', 'refundPercentage', parseInt(e.target.value) || 100)}
                      min="0" max="100"
                      className="w-14 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  )}
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={formData.replacementPolicy?.allowReplacement || false}
                    onChange={(e) => handleNestedChange('replacementPolicy', 'allowReplacement', e.target.checked)}
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-700">Allow Replacement</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={formData.warrantyConfiguration?.applicableToWarranty || false}
                    onChange={(e) => handleNestedChange('warrantyConfiguration', 'applicableToWarranty', e.target.checked)}
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-700">Warranty Applicable</span>
                </label>
              </div>

              {/* Reason Codes */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Reason Codes</label>
                  <button
                    type="button"
                    onClick={addReasonCode}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    + Add
                  </button>
                </div>
                {(formData.reasonCodes || []).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No reason codes added</p>
                ) : (
                  <div className="space-y-1">
                    {(formData.reasonCodes || []).map((rc, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <input
                          placeholder="Code"
                          value={rc.code || ''}
                          onChange={(e) => handleReasonCodeChange(index, 'code', e.target.value.toUpperCase())}
                          className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 uppercase"
                        />
                        <input
                          placeholder="Description"
                          value={rc.description || ''}
                          onChange={(e) => handleReasonCodeChange(index, 'description', e.target.value)}
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeReasonCode(index)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  returnType || isEditMode ? 'Update Return Type' : 'Create Return Type'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReturnTypesForm;
